import React from "react"
import { useTranslation } from "react-i18next"
import type { ToimipaikkaData as LocationData } from "../client"

interface PrintableFormViewProps {
  formState: {
    location?: string
    requestTypes: string[]
    descriptionRemoval?: string
    descriptionModification?: string
    descriptionAddition?: string
    descriptionPurchaseServiceCustomer?: string
    address?: string
    phoneNumber?: string
    email?: string
  }
  locationList: LocationData[]
}

export function PrintableFormView({ formState, locationList }: PrintableFormViewProps) {
  const { t } = useTranslation()

  // Find the location name
  const getLocationName = () => {
    if (!formState.location) return ""
    const loc = locationList.find((l) => l.id === formState.location)
    if (!loc) return formState.location
    const translationKey = `toimipaikkaNames.${loc.name}` as const
    return t(translationKey, { defaultValue: loc.name })
  }

  return (
    <div className="print-only max-w-4xl mx-auto p-8 bg-white text-black">
      {/* Header */}
      <div className="print-section print-avoid-break mb-6">
        <h1 className="text-2xl font-bold mb-4">{t("korjauspyyntoForm.title")}</h1>
        <p className="text-sm mb-4 p-1 border border-gray-300 rounded">
          {t("korjauspyyntoForm.gdprNotice")}
        </p>
      </div>

      {/* Section 1: Filled Form Data */}
      <div className="print-section print-avoid-break mb-6">
        <h2 className="text-xl font-bold mb-3">{t("korjauspyyntoForm.formInfo")}</h2>

        {/* Location */}
        <div className="mb-3">
          <strong>{t("korjauspyyntoForm.toimipaikkaLabel")}</strong>
          <p className="ml-4">{getLocationName()}</p>
        </div>

        {/* Request Types */}
        <div className="mb-3">
          <strong>{t("korjauspyyntoForm.requestTypesLabel")}</strong>
          <div className="ml-4">
            {formState.requestTypes.includes("removal") && (
              <div className="flex items-center mb-1">
                <span className="print-checkbox">✓</span>
                <span>{t("korjauspyyntoForm.requestTypePoistettaviksi")}</span>
              </div>
            )}
            {formState.requestTypes.includes("modification") && (
              <div className="flex items-center mb-1">
                <span className="print-checkbox">✓</span>
                <span>{t("korjauspyyntoForm.requestTypeMuutettaviksi")}</span>
              </div>
            )}
            {formState.requestTypes.includes("addition") && (
              <div className="flex items-center mb-1">
                <span className="print-checkbox">✓</span>
                <span>{t("korjauspyyntoForm.requestTypeLisattaviksi")}</span>
              </div>
            )}
            {formState.requestTypes.includes("purchaseServiceCustomer") && (
              <div className="flex items-center mb-1">
                <span className="print-checkbox">✓</span>
                <span>{t("korjauspyyntoForm.requestTypeOstopalveluasiakkaana")}</span>
              </div>
            )}
          </div>
        </div>

        {/* Conditional Descriptions */}
        {formState.requestTypes.includes("removal") && formState.descriptionRemoval && (
          <div className="mb-3">
            <strong>{t("korjauspyyntoForm.descriptionPoistettaviksiLabel")}</strong>
            <p className="ml-4 whitespace-pre-wrap">{formState.descriptionRemoval}</p>
          </div>
        )}

        {formState.requestTypes.includes("modification") && formState.descriptionModification && (
          <div className="mb-3">
            <strong>{t("korjauspyyntoForm.descriptionMuutettaviksiLabel")}</strong>
            <p className="ml-4 whitespace-pre-wrap">{formState.descriptionModification}</p>
          </div>
        )}

        {formState.requestTypes.includes("addition") && formState.descriptionAddition && (
          <div className="mb-3">
            <strong>{t("korjauspyyntoForm.descriptionLisattaviksiLabel")}</strong>
            <p className="ml-4 whitespace-pre-wrap">{formState.descriptionAddition}</p>
          </div>
        )}

        {formState.requestTypes.includes("purchaseServiceCustomer") &&
          formState.descriptionPurchaseServiceCustomer && (
            <div className="mb-3">
              <strong>{t("korjauspyyntoForm.descriptionOstopalveluasiakkaanaLabel")}</strong>
              <p className="ml-4 whitespace-pre-wrap">
                {formState.descriptionPurchaseServiceCustomer}
              </p>
            </div>
          )}

        {/* Contact Information */}
        {formState.address && (
          <div className="mb-3">
            <strong>{t("korjauspyyntoForm.addressLabel")}</strong>
            <p className="ml-4 whitespace-pre-wrap">{formState.address}</p>
          </div>
        )}

        {formState.phoneNumber && (
          <div className="mb-3">
            <strong>{t("korjauspyyntoForm.phoneNumberLabel")}</strong>
            <p className="ml-4">{formState.phoneNumber}</p>
          </div>
        )}

        {formState.email && (
          <div className="mb-3">
            <strong>{t("korjauspyyntoForm.emailLabel")}</strong>
            <p className="ml-4">{formState.email}</p>
          </div>
        )}
      </div>

      {/* Section 2: Signature Fields (to be filled by requester) */}
      <div className="print-section print-avoid-break mb-6 border-t-2 border-gray-400 pt-4">
        <h2 className="text-xl font-bold mb-3">Pyynnön tekijän tiedot</h2>

        <div className="mb-4">
          <strong>{t("korjauspyyntoForm.firstAndLastName")}</strong>
          <div className="print-blank-line mt-1"></div>
        </div>

        <div className="mb-4">
          <strong>{t("korjauspyyntoForm.personalIdentityCode")}</strong>
          <div className="print-blank-line mt-1"></div>
        </div>

        <div className="mb-4">
          <strong>{t("korjauspyyntoForm.dateField")}</strong>
          <div className="print-blank-line mt-1"></div>
        </div>

        <div className="mb-4">
          <strong>{t("korjauspyyntoForm.signature")}</strong>
          <div className="print-blank-line mt-1"></div>
        </div>
      </div>

      {/* Section 3: Identity Verification (to be filled by Terveystalo staff) */}
      <div className="print-section print-avoid-break mb-6 border-t-2 border-gray-400 pt-4">
        <h2 className="text-xl font-bold mb-3">
          {t("korjauspyyntoForm.identityVerificationTitle")}
        </h2>

        <div className="mb-4">
          <div className="flex items-center mb-2">
            <span className="print-checkbox"></span>
            <span>{t("korjauspyyntoForm.identityTypePassport")}</span>
          </div>
          <div className="flex items-center mb-2">
            <span className="print-checkbox"></span>
            <span>{t("korjauspyyntoForm.identityTypeDriversLicense")}</span>
          </div>
          <div className="flex items-center mb-2">
            <span className="print-checkbox"></span>
            <span>{t("korjauspyyntoForm.identityTypeIdCard")}</span>
          </div>
          <div className="flex items-center mb-2">
            <span className="print-checkbox"></span>
            <span>
              {t("korjauspyyntoForm.identityTypeOther")}{" "}
              <span className="print-blank-line" style={{ minWidth: "200px" }}></span>
            </span>
          </div>
        </div>

        <div className="mb-4">
          <strong>{t("korjauspyyntoForm.dateReceived")}</strong>
          <div className="print-blank-line mt-1"></div>
        </div>

        <div className="mb-4">
          <strong>{t("korjauspyyntoForm.employeeSignature")}</strong>
          <div className="print-blank-line mt-1"></div>
        </div>
      </div>

      {/* Section 4: Decision Field (to be filled by Terveystalo staff) */}
      <div className="print-section print-avoid-break mb-6 border-t-2 border-gray-400 pt-4">
        <h2 className="text-xl font-bold mb-3">{t("korjauspyyntoForm.decision")}</h2>
        <div
          className="border border-gray-400 p-2"
          style={{ minHeight: "150px", width: "100%" }}
        ></div>
      </div>

      {/* Section 5: Submission Instructions */}
      <div className="print-section print-avoid-break mt-6 p-4 border border-gray-400 bg-gray-50">
        <p className="text-sm font-bold">{t("korjauspyyntoForm.submissionInstructions")}</p>
      </div>
    </div>
  )
}
